import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPostJson } from './client'

/**
 * Securitee > XDR settings - `GET|POST /api/v2/securitee/xdr-settings`
 * (API: `App\Securitee\Xdr\XdrSettings`). Randul FIRMEI din `it_edr_settings`:
 * XDR pe toata firma si profilul agentului. 409 pe "All companies", 422 cu
 * `{error: 'field', field, channel?, message}` pe primul camp gresit.
 *
 * `?group=<id>`: acelasi ecran pentru un GRUP de aparate al firmei
 * (`it_edr_group_settings`), cu `inherited` si `company_settings`; la
 * salvare fiecare parte `null` = mostenita. 404 `{error: 'group'}` pentru un
 * grup care nu e al firmei din antet.
 */

export type XdrLimitKey = 'rate_per_minute' | 'queue_size' | 'process_interval' | 'net_interval'

export const XDR_LIMIT_KEYS: XdrLimitKey[] = ['rate_per_minute', 'queue_size', 'process_interval', 'net_interval']

export interface XdrChannel {
  name: string
  /** ID-urile pe care le primeste agentul (la un canal oprit: cele implicite). */
  ids: number[]
  /** Lista implicita; goala la un canal propriu al firmei. */
  default_ids: number[]
  on: boolean
  /** Canal pus pe firma din baza, in afara celor implicite. */
  custom: boolean
}

/** Un grup de aparate al firmei, pentru alegerea grupului. */
export interface XdrGroupOption {
  id: number
  name: string
  /** aparatele cu agent din grup */
  devices: number
  /** cate au XDR pornit: randul aparatului > grupul > firma */
  xdr_on: number
  /** grupul are setari proprii */
  customized: boolean
}

/** Pe ecranul unui grup: ce parte vine de la firma. */
export interface XdrInherited {
  enabled: boolean
  channels: boolean
  limits: Record<XdrLimitKey, boolean>
}

export interface XdrSettingsPage {
  /** firma din antet - a ei e setarea, si cu bifa "All companies" */
  company: { id: number; name: string }
  /** null = ecranul firmei; altfel grupul aratat */
  group?: { id: number; name: string } | null
  /** grupurile firmei, pentru alegerea grupului */
  groups?: XdrGroupOption[]
  /** numai pe ecranul unui grup */
  inherited?: XdrInherited
  /** numai pe ecranul unui grup: ce ar mosteni de la firma */
  company_settings?: { enabled: boolean; channels: XdrChannel[]; limits: Record<XdrLimitKey, number | null> }
  /** ce primeste agentul (pe grup: al grupului sau, mostenit, al firmei) */
  enabled: boolean
  channels: XdrChannel[]
  /** null = implicitul */
  limits: Record<XdrLimitKey, number | null>
  defaults: Record<XdrLimitKey, number>
  bounds: Record<XdrLimitKey, { min: number; max: number }>
  max_channels: number
  max_ids_per_channel: number
  devices: { total: number; xdr_on: number; own_row: number }
  updated_at: string | null
  updated_by_name: string
  can_edit: boolean
  /**
   * Omul e administrator de platforma: vede si sectiunea Wazuh connection
   * (`api/securiteeWazuhConnection.ts`). Vine si pe 403 / 409, in corpul erorii.
   */
  wazuh_admin?: boolean
}

export interface XdrSettingsSave {
  enabled: boolean
  channels: { name: string; on: boolean; ids: Array<number | string> }[]
  limits: Record<XdrLimitKey, number | string | null>
}

export const XDR_SETTINGS_KEY = ['securitee', 'xdr-settings'] as const

export function xdrSettingsQuery() {
  return queryOptions({
    queryKey: XDR_SETTINGS_KEY,
    queryFn: () => apiGet<XdrSettingsPage>('/securitee/xdr-settings'),
  })
}

export function saveXdrSettings(body: XdrSettingsSave): Promise<XdrSettingsPage> {
  return apiPostJson<XdrSettingsPage>('/securitee/xdr-settings', body)
}

/** Corpul unui grup: fiecare parte `null` = mostenita de la firma; totul null = grupul mosteneste tot. */
export interface XdrGroupSave {
  enabled: boolean | null
  channels: XdrSettingsSave['channels'] | null
  limits: Record<XdrLimitKey, number | string | null>
}

/** Toate ecranele de grup, ca sa se poata reciti dupa o salvare a firmei. */
export const XDR_GROUP_SETTINGS_KEY = ['securitee', 'xdr-settings', 'group'] as const

export function xdrGroupSettingsKey(groupId: number) {
  return [...XDR_GROUP_SETTINGS_KEY, groupId] as const
}

export function xdrGroupSettingsQuery(groupId: number) {
  return queryOptions({
    queryKey: xdrGroupSettingsKey(groupId),
    queryFn: () => apiGet<XdrSettingsPage>(`/securitee/xdr-settings?group=${groupId}`),
  })
}

export function saveXdrGroupSettings(groupId: number, body: XdrGroupSave): Promise<XdrSettingsPage> {
  return apiPostJson<XdrSettingsPage>(`/securitee/xdr-settings?group=${groupId}`, body)
}
